"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, useCallback } from "react";

// ---------------------------------------------------------------------------
// Binance WebSocket — free, no API key, no rate limits
// wss://stream.binance.com:9443/ws/<symbol>@kline_<interval>
// wss://stream.binance.com:9443/ws/<symbol>@ticker
// ---------------------------------------------------------------------------

export interface KlineData {
  time: number; // Unix seconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  isClosed: boolean;
}
export interface TickerData {
  symbol: string;
  price: number;
  priceChange: number;
  priceChangePercent: number;
  high24h: number;
  low24h: number;
  volume24h: number;
  quoteVolume24h: number;
}

// Map common crypto symbols to Binance trading pairs
const SYMBOL_MAP: Record<string, string> = {
  BTC: "BTCUSDT",
  ETH: "ETHUSDT",
  BNB: "BNBUSDT",
  SOL: "SOLUSDT",
  XRP: "XRPUSDT",
  ADA: "ADAUSDT",
  DOGE: "DOGEUSDT",
  DOT: "DOTUSDT",
  AVAX: "AVAXUSDT",
  MATIC: "MATICUSDT",
  LINK: "LINKUSDT",
  UNI: "UNIUSDT",
  ATOM: "ATOMUSDT",
  LTC: "LTCUSDT",
  NEAR: "NEARUSDT",
  APT: "APTUSDT",
  SUI: "SUIUSDT",
  ARB: "ARBUSDT",
  OP: "OPUSDT",
  FIL: "FILUSDT",
  TRX: "TRXUSDT",
  SHIB: "SHIBUSDT",
  TON: "TONUSDT",
  BCH: "BCHUSDT",
  XLM: "XLMUSDT",
  HBAR: "HBARUSDT",
  MKR: "MKRUSDT",
  AAVE: "AAVEUSDT",
  FTM: "FTMUSDT",
  ALGO: "ALGOUSDT",
  MANA: "MANAUSDT",
  GRT: "GRTUSDT",
  IMX: "IMXUSDT",
  ZK: "ZKUSDT",
  PEPE: "PEPEUSDT",
  RENDER: "RENDERUSDT",
  TAO: "TAOUSDT",
  INJ: "INJUSDT",
  SEI: "SEIUSDT",
  TIA: "TIAUSDT",
  STX: "STXUSDT",
  RNDR: "RNDRUSDT",
  FET: "FETUSDT",
  OCEAN: "OCEANUSDT",
  AGIX: "AGIXUSDT",
  ZEC: "ZECUSDT",
  XMR: "XMRUSDT",
  CRV: "CRVUSDT",
  SUSHI: "SUSHIUSDT",
  "1INCH": "1INCHUSDT",
  CAKE: "CAKEUSDT",
  COMP: "COMPUSDT",
  SNX: "SNXUSDT",
  LDO: "LDOUSDT",
  RPL: "RPLUSDT",
  JUP: "JUPUSDT",
  W: "WUSDT",
  FLOW: "FLOWUSDT",
  SAND: "SANDUSDT"
};
export type ChartInterval = "1m" | "3m" | "5m" | "15m" | "30m" | "1h" | "2h" | "4h" | "6h" | "8h" | "12h" | "1d" | "3d" | "1w" | "1M";
export function getBinanceSymbol(symbol: string): string | null {
  const upper = symbol.toUpperCase();
  // Direct match
  if (SYMBOL_MAP[upper]) return SYMBOL_MAP[upper]!;
  // Try as-is with USDT
  const candidate = `${upper}USDT`;
  return candidate;
}

/**
 * Hook for real-time candlestick (kline) data via Binance WebSocket.
 */
export function useBinanceKline(symbol: string, interval: ChartInterval = "1h") {
  const [klines, setKlines] = useState<KlineData[]>([]);
  const [currentKline, setCurrentKline] = useState<KlineData | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const binanceSymbol = getBinanceSymbol(symbol);

  // Fetch historical klines via REST (also free, no key needed)
  const fetchHistorical = useCallback(async () => {
    if (!binanceSymbol) return;
    try {
      const limit = 300;
      const url = `https://api.binance.com/api/v3/klines?symbol=${binanceSymbol}&interval=${interval}&limit=${limit}`;
      const res = await fetch(url);
      if (!res.ok) {
        // Symbol might not exist on Binance
        if (res.status === 400) {
          setError("not_available");
          return;
        }
        throw new Error(`Binance REST ${res.status}`);
      }
      const data = (await res.json()) as number[][];
      const parsed: KlineData[] = data.map(k => ({
        time: Math.floor((k[0] as number) / 1000),
        // ms to seconds
        open: parseFloat(k[1] as unknown as string),
        high: parseFloat(k[2] as unknown as string),
        low: parseFloat(k[3] as unknown as string),
        close: parseFloat(k[4] as unknown as string),
        volume: parseFloat(k[5] as unknown as string),
        isClosed: true
      }));
      setKlines(parsed);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch historical klines:", err);
      setError((err as Error).message);
    }
  }, [binanceSymbol, interval]);
  useEffect(() => {
    if (!binanceSymbol) return;

    // Fetch historical data first
    fetchHistorical();
    const pair = binanceSymbol.toLowerCase();
    const wsUrl = `wss://stream.binance.com:9443/ws/${pair}@kline_${interval}`;
    function connect() {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
      };
      ws.onmessage = event => {
        const msg = JSON.parse(event.data as string) as {
          k: {
            t: number; // Kline start time
            o: string; // Open
            h: string; // High
            l: string; // Low
            c: string; // Close
            v: string; // Volume
            x: boolean; // Is kline closed
          };
        };
        const k_0 = msg.k;
        const kline: KlineData = {
          time: Math.floor(k_0.t / 1000),
          open: parseFloat(k_0.o),
          high: parseFloat(k_0.h),
          low: parseFloat(k_0.l),
          close: parseFloat(k_0.c),
          volume: parseFloat(k_0.v),
          isClosed: k_0.x
        };
        setCurrentKline(kline);
        if (kline.isClosed) {
          setKlines(prev => {
            const filtered = prev.filter(p => p.time !== kline.time);
            return [...filtered, kline];
          });
        }
      };
      ws.onclose = () => {
        setIsConnected(false);
        // Reconnect after 3 seconds
        reconnectTimerRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => {
        ws.close();
      };
    }
    connect();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent reconnect on intentional close
        wsRef.current.close();
      }
    };
  }, [binanceSymbol, interval, fetchHistorical]);
  return {
    klines,
    currentKline,
    isConnected,
    error
  };
}

/**
 * Hook for real-time ticker (price) data via Binance WebSocket.
 */
export function useBinanceTicker(symbol) {
  const $ = _c(8);
  const [ticker, setTicker] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  let t0;
  if ($[0] !== symbol) {
    t0 = getBinanceSymbol(symbol);
    $[0] = symbol;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const binanceSymbol = t0;
  let t1;
  let t2;
  if ($[2] !== binanceSymbol) {
    t1 = () => {
      if (!binanceSymbol) {
        return;
      }
      const pair = binanceSymbol.toLowerCase();
      const wsUrl = `wss://stream.binance.com:9443/ws/${pair}@ticker`;
      function connect() {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => setIsConnected(true);
        ws.onmessage = event => {
          const msg = JSON.parse(event.data as string) as {
            s: string; // Symbol
            c: string; // Last price
            p: string; // Price change
            P: string; // Price change percent
            h: string; // High
            l: string; // Low
            v: string; // Volume
            q: string; // Quote volume
          };
          const price = parseFloat(msg.c);
          if (isNaN(price)) {
            return;
          }
          setTicker({
            symbol: msg.s,
            price,
            priceChange: parseFloat(msg.p) || 0,
            priceChangePercent: parseFloat(msg.P) || 0,
            high24h: parseFloat(msg.h) || 0,
            low24h: parseFloat(msg.l) || 0,
            volume24h: parseFloat(msg.v) || 0,
            quoteVolume24h: parseFloat(msg.q) || 0
          });
        };
        ws.onclose = () => {
          setIsConnected(false);
          reconnectTimerRef.current = setTimeout(connect, 3000);
        };
        ws.onerror = () => ws.close();
      }
      connect();
      return () => {
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
        }
        if (wsRef.current) {
          wsRef.current.onclose = null;
          wsRef.current.close();
        }
      };
    };
    t2 = [binanceSymbol];
    $[2] = binanceSymbol;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== isConnected || $[6] !== ticker) {
    t3 = {
      ticker,
      isConnected
    };
    $[5] = isConnected;
    $[6] = ticker;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}